import React, {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {Loader2} from 'lucide-react';
import ProviderLayout from '@/modules/provider-console/components/ProviderLayout';
import PatientBanner from '@/modules/order-tests/components/PatientBanner';
import {resolveManualTestElisPanelId} from '@/modules/order-tests/utils/resolveWrapperPanel';
import PatientEditModal from '@/modules/provider-console/components/PatientEditModal';
import TestCatalog, {
    createEmptySelection,
    deserializeSelection,
    toggleProfile,
    togglePanel,
    toggleTest,
    type CatalogSelection,
} from '@/modules/order-tests/components/TestCatalog';
import CollectionDetails from '@/modules/order-tests/components/CollectionDetails';
import OrderFooter from '@/modules/order-tests/components/OrderFooter';
import {ProviderOrderTestsService} from '@/modules/order-tests/services/providerOrderTestsService';
import type {SaveOrderRequest} from '@/modules/order-tests/services/providerOrderTestsService';
import type {OrderTestsQueuedPatient, OrderDraftData, CalculatedValue} from '@/shared/types/api';

const ProviderOrderBuilder: React.FC = () => {
    const {userId} = useParams<{userId: string}>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const parsedUserId = userId ? Number(userId) : 0;

    // Fetch queue to get patient info
    const queueQuery = useQuery({
        queryKey: ['order-tests-queue'],
        queryFn: () => ProviderOrderTestsService.getQueue(),
    });

    const patient: OrderTestsQueuedPatient | null = useMemo(() => {
        return queueQuery.data?.queue.find((p) => p.userId === parsedUserId) ?? null;
    }, [queueQuery.data, parsedUserId]);

    // Fetch saved draft data from our DB
    const draftQuery = useQuery({
        queryKey: ['order-draft', parsedUserId],
        queryFn: () => ProviderOrderTestsService.getDraft(parsedUserId),
        enabled: parsedUserId > 0,
        staleTime: Infinity,
    });

    // Create draft order in ELIS (only if no saved draft exists)
    const draftMutation = useMutation({
        mutationFn: () => ProviderOrderTestsService.createDraftOrder(),
        onError: () => toast.error('Failed to create draft order in ELIS. Save/submit will not work.'),
    });

    // Auto-create ELIS draft on mount — skip if we have a saved draft with an elisOrderId
    const draftAttempted = React.useRef(false);
    const savedDraft = draftQuery.data?.draft ?? null;
    const hasSavedElisOrderId = savedDraft?.elisOrderId != null && savedDraft.elisOrderId > 0;

    React.useEffect(() => {
        if (!draftAttempted.current && patient && !draftQuery.isLoading && !hasSavedElisOrderId) {
            draftAttempted.current = true;
            draftMutation.mutate();
        }
    }, [patient, draftQuery.isLoading, hasSavedElisOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

    const elisOrderId = hasSavedElisOrderId ? savedDraft!.elisOrderId : (draftMutation.data?.elisOrderId ?? null);

    // Fetch catalog + reference data
    const catalogQuery = useQuery({
        queryKey: ['order-tests-catalog'],
        queryFn: () => ProviderOrderTestsService.getCatalog(),
        staleTime: 5 * 60 * 1000,
    });

    const specimenTypesQuery = useQuery({
        queryKey: ['order-tests-specimen-types'],
        queryFn: () => ProviderOrderTestsService.getSpecimenTypes(),
        staleTime: 5 * 60 * 1000,
    });

    const collectorsQuery = useQuery({
        queryKey: ['order-tests-collectors'],
        queryFn: () => ProviderOrderTestsService.getCollectors(),
        staleTime: 5 * 60 * 1000,
    });

    // Selection state
    const [selection, setSelection] = useState<CatalogSelection>(createEmptySelection);

    const handleToggleProfile = useCallback((profileId: number) => {
        if (!catalogQuery.data) return;
        setSelection((prev) => toggleProfile(catalogQuery.data!, prev, profileId));
    }, [catalogQuery.data]);

    const handleTogglePanel = useCallback((panelId: number) => {
        if (!catalogQuery.data) return;
        setSelection((prev) => togglePanel(catalogQuery.data!, prev, panelId));
    }, [catalogQuery.data]);

    const handleToggleTest = useCallback((testId: number) => {
        setSelection((prev) => toggleTest(prev, testId));
    }, []);

    // Reverse flow: selecting a calculated value auto-adds its prerequisite tests
    const handleSelectCalcValue = useCallback((code: string) => {
        if (!catalogQuery.data) return;
        const cvMap = new Map(catalogQuery.data.calculatedValues.map((cv) => [cv.code, cv]));
        const target = cvMap.get(code);
        if (!target) return;

        // Recursively resolve prerequisites — expand calc value prereqs to leaf tests
        const leafPrereqs = new Set<string>();
        const resolve = (prereqs: string[]) => {
            for (const p of prereqs) {
                const nested = cvMap.get(p);
                if (nested) resolve(nested.prerequisites);
                else leafPrereqs.add(p);
            }
        };
        resolve(target.prerequisites);

        // Find test IDs for each leaf prerequisite assay code
        const seen = new Set<number>();
        const testIds: number[] = [];
        // Check assayTests first (individual tests from assay catalog)
        for (const test of catalogQuery.data.assayTests ?? []) {
            if (test.id > 0 && test.assayCode && leafPrereqs.has(test.assayCode) && !seen.has(test.id)) {
                testIds.push(test.id);
                seen.add(test.id);
            }
        }
        // Fallback: check panel tests
        for (const panel of catalogQuery.data.panels) {
            for (const test of panel.tests) {
                if (test.assayCode && leafPrereqs.has(test.assayCode) && !seen.has(test.id)) {
                    testIds.push(test.id);
                    seen.add(test.id);
                }
            }
        }

        // Add all prerequisite tests to the selection
        setSelection((prev) => {
            const next: CatalogSelection = {
                selectedProfileIds: new Set(prev.selectedProfileIds),
                selectedPanelIds: new Set(prev.selectedPanelIds),
                selectedTestIds: new Set(prev.selectedTestIds),
                panelSource: new Map(prev.panelSource),
                testSource: new Map(prev.testSource),
            };
            for (const tid of testIds) {
                if (!next.selectedTestIds.has(tid)) {
                    next.selectedTestIds.add(tid);
                    next.testSource.set(tid, {source: 'manual'});
                }
            }
            return next;
        });
    }, [catalogQuery.data]);

    // Compute auto-added calculated values based on selected tests
    const autoAddedCalcValues = useMemo((): CalculatedValue[] => {
        if (!catalogQuery.data?.calculatedValues?.length) return [];

        // Build set of assay codes for all selected tests
        const selectedAssayCodes = new Set<string>();
        for (const testId of selection.selectedTestIds) {
            // Check assayTests (individual tests from assay catalog)
            const assayTest = catalogQuery.data.assayTests?.find((t) => t.id === testId);
            if (assayTest?.assayCode) {
                selectedAssayCodes.add(assayTest.assayCode);
                continue;
            }
            // Check panel tests
            for (const panel of catalogQuery.data.panels) {
                for (const test of panel.tests) {
                    if (test.id === testId && test.assayCode) {
                        selectedAssayCodes.add(test.assayCode);
                    }
                }
            }
        }

        // Iterative resolution: keep looping until stable (handles chaining)
        const satisfied: CalculatedValue[] = [];
        const satisfiedCodes = new Set<string>();
        let changed = true;
        while (changed) {
            changed = false;
            for (const cv of catalogQuery.data.calculatedValues) {
                if (satisfiedCodes.has(cv.code)) continue;
                const allMet = cv.prerequisites.every(
                    (prereq) => selectedAssayCodes.has(prereq) || satisfiedCodes.has(prereq)
                );
                if (allMet) {
                    satisfied.push(cv);
                    satisfiedCodes.add(cv.code);
                    changed = true;
                }
            }
        }
        return satisfied;
    }, [selection.selectedTestIds, catalogQuery.data]);

    // Reconcile: remove calculated tests from selection when their prerequisites are no longer met
    useEffect(() => {
        if (!catalogQuery.data?.calculatedValues?.length) return;

        // Build set of satisfied calc codes
        const satisfiedCodes = new Set(autoAddedCalcValues.map((cv) => cv.code));

        // Find calculated tests in selectedTestIds whose prerequisites are NOT satisfied
        const toRemove: number[] = [];
        for (const testId of selection.selectedTestIds) {
            // Check assayTests first
            const assayTest = catalogQuery.data.assayTests?.find((t) => t.id === testId);
            if (assayTest?.isCalculated) {
                const code = assayTest.assayCode || assayTest.code;
                if (!satisfiedCodes.has(code)) toRemove.push(testId);
                continue;
            }
            // Check panel tests
            for (const panel of catalogQuery.data.panels) {
                for (const test of panel.tests) {
                    if (test.id === testId && test.isCalculated) {
                        const code = test.assayCode || test.code;
                        if (!satisfiedCodes.has(code)) {
                            toRemove.push(testId);
                        }
                    }
                }
            }
        }

        if (toRemove.length > 0) {
            setSelection((prev) => {
                const next: CatalogSelection = {
                    selectedProfileIds: new Set(prev.selectedProfileIds),
                    selectedPanelIds: new Set(prev.selectedPanelIds),
                    selectedTestIds: new Set(prev.selectedTestIds),
                    panelSource: new Map(prev.panelSource),
                    testSource: new Map(prev.testSource),
                };
                for (const tid of toRemove) {
                    next.selectedTestIds.delete(tid);
                    next.testSource.delete(tid);
                }
                return next;
            });
        }
    }, [autoAddedCalcValues, selection.selectedTestIds, catalogQuery.data]);

    // Compute running order total from selected tests' addon prices
    const {orderTotal, hasTbdTests} = useMemo((): {orderTotal: number; hasTbdTests: boolean} => {
        if (!catalogQuery.data) return {orderTotal: 0, hasTbdTests: false};
        let total = 0;
        let tbd = false;
        const counted = new Set<number>();
        for (const testId of selection.selectedTestIds) {
            if (counted.has(testId)) continue;
            counted.add(testId);
            for (const panel of catalogQuery.data.panels) {
                const test = panel.tests.find((t) => t.id === testId);
                if (test) {
                    total += test.addonPrice;
                    if (test.classification === 4) tbd = true;
                    break;
                }
            }
        }
        return {orderTotal: Math.round(total * 100) / 100, hasTbdTests: tbd};
    }, [selection.selectedTestIds, catalogQuery.data]);

    // Collection details state
    const [specimenTypeId, setSpecimenTypeId] = useState<number | null>(null);
    const [additionalSpecimenTypeIds, setAdditionalSpecimenTypeIds] = useState<number[]>([]);
    const [collectorId, setCollectorId] = useState<number | null>(null);
    const [collectionDate, setCollectionDate] = useState('');
    const [collectionTime, setCollectionTime] = useState('');
    const [notes, setNotes] = useState('');
    const [isPatientEditOpen, setIsPatientEditOpen] = useState(false);

    const handleCollectionChange = useCallback((field: string, value: string | number) => {
        switch (field) {
            case 'specimenTypeId': setSpecimenTypeId(Number(value) || null); break;
            case 'collectorId': setCollectorId(Number(value) || null); break;
            case 'collectionDate': setCollectionDate(String(value)); break;
            case 'collectionTime': setCollectionTime(String(value)); break;
            case 'notes': setNotes(String(value)); break;
        }
    }, []);

    const handleAddSpecimenType = useCallback(() => {
        setAdditionalSpecimenTypeIds((prev) => [...prev, 0]);
    }, []);

    const handleRemoveSpecimenType = useCallback((index: number) => {
        setAdditionalSpecimenTypeIds((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleChangeAdditionalSpecimenType = useCallback((index: number, value: number) => {
        setAdditionalSpecimenTypeIds((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    }, []);

    // Restore draft state from saved data
    const draftRestored = useRef(false);
    useEffect(() => {
        if (draftRestored.current || !savedDraft || !catalogQuery.data) return;
        draftRestored.current = true;

        // Restore selection state, filtering to only IDs that exist in the current catalog
        const restored = deserializeSelection(savedDraft);
        const validTestIds = new Set<number>();
        const allCatalogTestIds = new Set<number>();
        for (const panel of catalogQuery.data.panels) {
            for (const test of panel.tests) allCatalogTestIds.add(test.id);
        }
        for (const test of catalogQuery.data.assayTests ?? []) {
            if (test.id > 0) allCatalogTestIds.add(test.id);
        }
        for (const id of restored.selectedTestIds) {
            if (allCatalogTestIds.has(id)) validTestIds.add(id);
        }
        restored.selectedTestIds = validTestIds;
        setSelection(restored);

        // Restore collection details
        if (savedDraft.collectionSpecimenTypeId != null) {
            setSpecimenTypeId(savedDraft.collectionSpecimenTypeId);
        }
        if (savedDraft.additionalSpecimenTypeIds?.length) {
            setAdditionalSpecimenTypeIds(savedDraft.additionalSpecimenTypeIds);
        }
        if (savedDraft.collectorId != null) {
            setCollectorId(savedDraft.collectorId);
        }
        if (savedDraft.collectionDate) {
            setCollectionDate(savedDraft.collectionDate);
        }
        if (savedDraft.collectionTime) {
            setCollectionTime(savedDraft.collectionTime);
        }
        if (typeof savedDraft.notes === 'string') {
            setNotes(savedDraft.notes);
        }
    }, [savedDraft]);

    // Current draft data — always reflects latest state
    const currentDraftData = useMemo((): OrderDraftData | null => {
        if (!elisOrderId) return null;
        const panelSource: Record<string, string> = {};
        for (const [key, value] of selection.panelSource) {
            panelSource[String(key)] = value;
        }
        const testSource: Record<string, {source: string; panelId?: number; panelName?: string}> = {};
        for (const [key, value] of selection.testSource) {
            testSource[String(key)] = value;
        }
        const collector = collectorsQuery.data?.collectors.find((c) => c.elisUserId === collectorId);
        return {
            elisOrderId,
            selectedProfileIds: [...selection.selectedProfileIds],
            selectedPanelIds: [...selection.selectedPanelIds],
            selectedTestIds: [...selection.selectedTestIds],
            panelSource,
            testSource,
            collectionSpecimenTypeId: specimenTypeId,
            additionalSpecimenTypeIds: additionalSpecimenTypeIds.filter((id) => id > 0),
            collectorId,
            collectorName: collector?.name ?? '',
            collectionDate,
            collectionTime,
            notes,
        };
    }, [elisOrderId, selection, specimenTypeId, additionalSpecimenTypeIds, collectorId, collectionDate, collectionTime, notes, collectorsQuery.data]);

    // Ref always points to the latest draft data — timer callbacks and unmount
    // read from this ref to avoid stale closures
    const draftDataRef = useRef<OrderDraftData | null>(null);
    draftDataRef.current = currentDraftData;

    // Auto-save draft data to our DB on any state change (debounced 1.5s)
    // On unmount, flush immediately so no changes are lost.
    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Don't auto-save until draft restore has completed (or there's no draft to restore)
        if (!draftRestored.current && savedDraft) return;
        if (draftQuery.isLoading) return;
        if (parsedUserId <= 0) return;

        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

        autoSaveTimer.current = setTimeout(() => {
            // Read from ref so we always get the latest state (avoids stale closures)
            const draftData = draftDataRef.current;
            if (draftData) {
                ProviderOrderTestsService.saveDraft(parsedUserId, draftData).catch(() => {
                    // Silent failure — auto-save is best-effort
                });
            }
        }, 1500);

        return () => {
            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        };
    }, [selection, specimenTypeId, additionalSpecimenTypeIds, collectorId, collectionDate, collectionTime, elisOrderId, parsedUserId, draftQuery.isLoading, savedDraft]);

    // Flush any pending auto-save on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
            const draftData = draftDataRef.current;
            if (draftData && parsedUserId > 0) {
                ProviderOrderTestsService.saveDraft(parsedUserId, draftData).catch(() => {});
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Build save payload — collection details are optional for draft saves
    const buildPayload = useCallback((requireCollectionDetails: boolean): SaveOrderRequest | null => {
        if (!elisOrderId || !patient) return null;

        const collector = collectorsQuery.data?.collectors.find((c) => c.elisUserId === collectorId);

        if (requireCollectionDetails) {
            if (!collector || !specimenTypeId) return null;
        }

        // Convert date from YYYY-MM-DD to MM/DD/YYYY for ELIS
        const dateParts = collectionDate.split('-');
        const elisDate = dateParts.length === 3 ? `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}` : collectionDate;

        // Resolve additional specimen type names for ELIS
        const additionalSpecimens = additionalSpecimenTypeIds
            .filter((id) => id > 0)
            .map((id) => {
                const st = specimenTypesQuery.data?.specimenTypes.find((s) => s.elisSpecimenTypeId === id);
                return {id, name: st?.elisName ?? ''};
            })
            .filter((s) => s.name !== '');

        // Resolve selections to ELIS panel IDs
        const elisPanelIds = new Set<number>();
        const catalogData = catalogQuery.data;

        if (catalogData) {
            // Panels from cascade (profile + manual panel selections)
            for (const panelId of selection.selectedPanelIds) {
                const panel = catalogData.panels.find((p) => p.id === panelId);
                if (panel) elisPanelIds.add(panel.elisPanelId);
            }

            // Manual tests → the canonical wrapper panel the server resolved
            // (provider-enabled variant first); legacy first-match scan only
            // for catalogs cached from an older API. See resolveWrapperPanel.
            for (const testId of selection.selectedTestIds) {
                if (selection.testSource.get(testId)?.source === 'manual') {
                    const elisPanelId = resolveManualTestElisPanelId(catalogData, testId);
                    if (elisPanelId != null) elisPanelIds.add(elisPanelId);
                }
            }
        }

        // Add ELIS panel IDs for auto-added calculated values
        for (const cv of autoAddedCalcValues) {
            if (cv.elisPanelId != null) {
                elisPanelIds.add(cv.elisPanelId);
            }
        }

        return {
            elisOrderId,
            dashboardUserId: patient.userId,
            patient: {
                elisPatientId: patient.elisPatientId ?? undefined,
                firstName: patient.firstName,
                lastName: patient.lastName,
                birthDate: patient.birthDate,
                gender: patient.gender,
            },
            selectedTestIds: [...selection.selectedTestIds],
            selectedElisPanelIds: elisPanelIds.size > 0 ? [...elisPanelIds] : undefined,
            collectionSpecimenTypeID: specimenTypeId ?? 0,
            additionalSpecimens: additionalSpecimens.length > 0 ? additionalSpecimens : undefined,
            collector: collector
                ? {elisUserId: collector.elisUserId, name: collector.name}
                : {elisUserId: 0, name: ''},
            collectionDate: elisDate,
            collectionTime,
            notes: notes.trim() || undefined,
        };
    }, [elisOrderId, patient, selection, specimenTypeId, additionalSpecimenTypeIds, collectorId, collectionDate, collectionTime, notes, collectorsQuery.data, specimenTypesQuery.data, catalogQuery.data, autoAddedCalcValues]);

    // Mutations
    // Surface the backend's specific error message when available so providers
    // see something they can act on instead of a generic banner.
    const messageFromError = (err: unknown, fallback: string): string => {
        if (err && typeof err === 'object' && 'message' in err) {
            const msg = (err as {message?: unknown}).message;
            if (typeof msg === 'string' && msg.trim().length > 0) return msg;
        }
        return fallback;
    };

    const saveMutation = useMutation({
        mutationFn: (data: OrderDraftData) => ProviderOrderTestsService.saveDraft(parsedUserId, data, true),
        onSuccess: (result) => {
            if (result.elisError) {
                toast.warn('Draft saved locally, but failed to sync to ELIS. Try again.');
            } else {
                toast.success('Draft saved');
            }
        },
        onError: (err) => toast.error(messageFromError(err, 'Failed to save draft')),
    });

    const submitMutation = useMutation({
        mutationFn: (data: SaveOrderRequest) => ProviderOrderTestsService.submitOrder(data),
        onSuccess: async () => {
            toast.success('Order submitted');
            // Remove patient from queue and clear stale draft cache
            if (patient) {
                await ProviderOrderTestsService.removeFromQueue(patient.userId);
                queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
                queryClient.removeQueries({queryKey: ['order-draft', parsedUserId]});
            }
            navigate('/provider/order-tests');
        },
        onError: (err) => toast.error(messageFromError(err, 'Failed to submit order')),
    });

    const canSubmit = selection.selectedTestIds.size > 0
        && specimenTypeId != null
        && collectorId != null
        && collectionDate !== ''
        && collectionTime !== ''
        && elisOrderId != null;

    const handleSaveDraft = () => {
        const draftData = draftDataRef.current;
        if (!draftData) {
            if (!elisOrderId) {
                toast.error('Draft order not yet created. Please wait or refresh the page.');
            }
            return;
        }
        // Cancel any pending auto-save timer to avoid a race
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        // Single mutation: saves to local DB + ELIS via ?saveToElis=1
        saveMutation.mutate(draftData);
    };

    const handleSubmit = () => {
        // Preflight: check patient demographics before hitting the API so the
        // provider sees a specific, actionable message instead of a generic 400.
        if (patient) {
            const missing: string[] = [];
            if (!patient.firstName) missing.push('first name');
            if (!patient.lastName) missing.push('last name');
            if (!patient.birthDate) missing.push('date of birth');
            if (!patient.gender) missing.push('gender');
            if (missing.length > 0) {
                toast.error(
                    `Cannot submit: patient is missing ${missing.join(', ')}. Update the patient record and try again.`
                );
                return;
            }
        }

        const payload = buildPayload(true);
        if (!payload) {
            toast.error('Please complete all collection details before submitting.');
            return;
        }
        submitMutation.mutate(payload);
    };

    // Loading / error states
    if (!parsedUserId) {
        return (
            <ProviderLayout title="Order Tests" subtitle="Create and manage lab orders">
                <div className="p-6 text-sm text-mld-danger">Invalid patient ID.</div>
            </ProviderLayout>
        );
    }

    if (queueQuery.isLoading || catalogQuery.isLoading || draftQuery.isLoading) {
        return (
            <ProviderLayout title="Order Tests" subtitle="Create and manage lab orders">
                <div className="p-6 text-sm text-gray-500">Loading...</div>
            </ProviderLayout>
        );
    }

    // If patient not in cached queue but a refetch is in progress, wait for it
    if (!patient && queueQuery.isFetching) {
        return (
            <ProviderLayout title="Order Tests" subtitle="Create and manage lab orders">
                <div className="p-6 text-sm text-gray-500">Loading...</div>
            </ProviderLayout>
        );
    }

    if (!patient) {
        return (
            <ProviderLayout title="Order Tests" subtitle="Create and manage lab orders">
                <div className="p-6 text-sm text-gray-500">
                    Patient not found in queue.{' '}
                    <button type="button" onClick={() => navigate('/provider/order-tests')} className="text-mld-primary hover:underline">
                        Back to queue
                    </button>
                </div>
            </ProviderLayout>
        );
    }

    return (
        <ProviderLayout title="Order Tests" subtitle="Create and manage lab orders">
            <div className="mx-auto max-w-4xl space-y-6 px-4 pb-24">
                {/* Patient banner */}
                <PatientBanner
                    patient={patient}
                    status={draftMutation.data?.status ?? (hasSavedElisOrderId ? 'Draft (saved)' : null)}
                    isConnecting={draftMutation.isPending}
                    onEdit={() => setIsPatientEditOpen(true)}
                />
                <PatientEditModal
                    isOpen={isPatientEditOpen}
                    userId={patient?.userId ?? null}
                    onClose={() => setIsPatientEditOpen(false)}
                    onSaved={() => {
                        // Refetch the queue so the banner picks up updated
                        // gender/DOB/name, which buildPayload reads from.
                        queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
                    }}
                />


                {draftMutation.isError && (
                    <p className="text-sm text-mld-danger">Failed to create draft order. Please try again.</p>
                )}

                {/* Auto-save banner */}
                <div className="flex items-center justify-between rounded-lg border border-mld-warning/40 bg-mld-warning/5 px-4 py-2.5">
                    <p className="text-sm text-mld-warning">
                        {draftMutation.isPending ? (
                            <>
                                <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />
                                <span className="font-medium">Connecting to lab system</span>
                                {' — you can start selecting tests while this completes.'}
                            </>
                        ) : (
                            <>
                                <span className="font-medium">Auto-save available</span>
                                {' — save this order as a Draft at any time to return later.'}
                            </>
                        )}
                    </p>
                    <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={saveMutation.isPending || selection.selectedTestIds.size === 0 || !elisOrderId}
                        className="shrink-0 rounded-md bg-mld-warning/50 px-3 py-1.5 text-xs font-semibold text-white hover:bg-mld-warning/90 disabled:opacity-50 transition-colors">
                        {saveMutation.isPending ? 'Saving...' : 'Save Draft'}
                    </button>
                </div>

                {/* Test catalog */}
                {catalogQuery.data && (
                    <TestCatalog
                        catalog={catalogQuery.data}
                        selection={selection}
                        autoAddedCalcValues={autoAddedCalcValues}
                        onToggleProfile={handleToggleProfile}
                        onTogglePanel={handleTogglePanel}
                        onToggleTest={handleToggleTest}
                        onSelectCalcValue={handleSelectCalcValue}
                    />
                )}

                {/* Collection details */}
                <CollectionDetails
                    specimenTypes={specimenTypesQuery.data?.specimenTypes ?? []}
                    collectors={collectorsQuery.data?.collectors ?? []}
                    specimenTypeId={specimenTypeId}
                    additionalSpecimenTypeIds={additionalSpecimenTypeIds}
                    onAddSpecimenType={handleAddSpecimenType}
                    onRemoveSpecimenType={handleRemoveSpecimenType}
                    onChangeAdditionalSpecimenType={handleChangeAdditionalSpecimenType}
                    collectorId={collectorId}
                    collectionDate={collectionDate}
                    collectionTime={collectionTime}
                    notes={notes}
                    onChange={handleCollectionChange}
                />
            </div>

            {/* Footer */}
            <OrderFooter
                selectedProfileCount={selection.selectedProfileIds.size}
                manualPanelCount={[...selection.panelSource.values()].filter((s) => s === 'panel').length}
                selectedTestCount={selection.selectedTestIds.size}
                orderTotal={orderTotal}
                hasTbdTests={hasTbdTests}
                canSubmit={canSubmit}
                isSaving={saveMutation.isPending}
                isSubmitting={submitMutation.isPending}
                isConnecting={draftMutation.isPending}
                onCancel={() => navigate('/provider/order-tests')}
                onSaveDraft={handleSaveDraft}
                onSubmit={handleSubmit}
            />
        </ProviderLayout>
    );
};

export default ProviderOrderBuilder;
